import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconErrorSolid from '@apollo/icons/default/IconErrorSolid.svg';
import IconExpandList from '@apollo/icons/default/IconExpandList.svg';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Link,
  Menu,
  MenuButton,
  MenuContent,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  Spinner,
} from '@apollo/orbit';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { NoData } from 'src/components/common/noData/NoData';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { format } from 'src/lib/format';
import { TraceFragment, TraceNodeKind } from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import { Trace } from './trace/Trace';
import { TraceTree, TraceTreeNode } from './trace/TraceTree';

enum AutoExpandMode {
  CRITICAL_PATH = 'CRITICAL_PATH',
  ALL_ERRORS = 'ALL_ERRORS',
  NONE = 'NONE',
}

function TraceIncomplete() {
  return (
    <ErrorMessage className="mb-10">
      This trace is incomplete or formatted in a way that is not supported. It's
      possible that the GraphQL server you are using is not fully compatible
      with the{' '}
      <Link
        isExternal
        to={DocsRouter.path('UsageReportingWithHash', {
          hash: 'enabling-traces',
        })}
      >
        Apollo Tracing Format.
      </Link>{' '}
      For additional assistance,{' '}
      <ContactSupportLink>contact support</ContactSupportLink>.
    </ErrorMessage>
  );
}

const QueryTraceContent = ({
  traceTree,
  loading,
  hasOtherTraces,
  onOpenChange,
  openNodes,
}: {
  traceTree: TraceTree | null;
  loading: boolean;
  hasOtherTraces: boolean;
  openNodes: Set<string>;
  onOpenChange: (nodeId: string) => void;
}) => {
  if (loading) {
    return (
      <div className="flex h-60 w-full items-center justify-center">
        <Spinner size="md" data-testid="spinner" />
      </div>
    );
  }

  if (!traceTree) {
    return (
      <NoData
        message="Trace not found"
        secondary={
          hasOtherTraces
            ? 'Try clicking a bar on the performance distribution.'
            : 'There are no recorded traces for this operation.'
        }
      />
    );
  }

  return (
    <ErrorBoundary renderError={() => <TraceIncomplete />}>
      {traceTree.isIncomplete ? (
        <TraceIncomplete />
      ) : (
        <Trace
          traceTree={traceTree}
          openNodes={openNodes}
          onOpenChange={onOpenChange}
        />
      )}
    </ErrorBoundary>
  );
};

/**
 * When internal router spans are hidden, there are still some that
 * we want to preserve.  Possibly this is something that the backend
 * could indicate but leaving it here for now.
 */
const preservedInternalSpans = ['fetch', 'subscribe'];

export const onOpenChange = (
  nodeId: string,
  openNodes: Set<string>,
  setOpenNodesSet: React.Dispatch<React.SetStateAction<Set<string>>>,
  setAutoExpandMode?: (value: React.SetStateAction<AutoExpandMode>) => void,
) => {
  if (openNodes.has(nodeId)) {
    setOpenNodesSet((prev) => {
      prev.delete(nodeId);
      return new Set(prev);
    });
  } else {
    setOpenNodesSet((prev) => new Set(prev.add(nodeId)));
  }
  if (setAutoExpandMode) setAutoExpandMode(AutoExpandMode.NONE);
};

export const QueryTraceCard = ({
  trace,
  loading,
  hasOtherTraces,
}: {
  trace: TraceFragment | null;
  loading: boolean;
  hasOtherTraces: boolean;
}) => {
  const [routerInternalsSelection, setRouterInternalSelection] = useState<
    'show' | 'hide'
  >('show');
  const [autoExpandMode, setAutoExpandMode] = useState<AutoExpandMode>(
    AutoExpandMode.CRITICAL_PATH,
  );

  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());

  const filter = useCallback(
    (node: TraceTreeNode) =>
      routerInternalsSelection === 'show' ||
      node.kind !== TraceNodeKind.ROUTER_INTERNAL ||
      (typeof node.key === 'string' &&
        preservedInternalSpans.includes(node.key)),
    [routerInternalsSelection],
  );

  const traceTree = useMemo(
    () => (trace !== null ? new TraceTree(trace, filter) : null),
    [trace, filter],
  );
  const traceErrorsCount = traceTree?.erroredNodes.length ?? 0;

  const onOpenChangeCallback = useCallback(
    (nodeId: string) =>
      onOpenChange(nodeId, openNodes, setOpenNodes, setAutoExpandMode),
    [openNodes],
  );

  useEffect(() => {
    if (
      autoExpandMode === AutoExpandMode.CRITICAL_PATH &&
      traceTree?.criticalPath
    ) {
      setOpenNodes(new Set(traceTree?.criticalPath.map((n) => n.id)));
    } else if (autoExpandMode === AutoExpandMode.ALL_ERRORS) {
      setOpenNodes(
        new Set(
          traceTree?.erroredNodes.map((n) => n.path.map((p) => p.id)).flat(),
        ),
      );
    }
  }, [autoExpandMode, traceTree?.criticalPath, traceTree?.erroredNodes]);

  return (
    <Card variant="unstyled" className="mt-8">
      <CardHeader>
        <CardTitle
          className="min-h-8" /* prevent layout shift when changing traces */
        >
          Execution Path{' '}
          {traceErrorsCount > 0 && (
            <Badge variant="error" icon={<IconErrorSolid />}>
              {traceErrorsCount} {format.pluralize(traceErrorsCount, 'error')}
            </Badge>
          )}
        </CardTitle>
        <ButtonGroup>
          {traceTree && !traceTree?.isIncomplete && (
            <Menu autoSelect={false}>
              <MenuButton
                as={Button}
                variant="secondary"
                size="sm"
                rightIcon={<IconChevronDown />}
              >
                Trace View
              </MenuButton>
              <MenuContent>
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      setRouterInternalSelection('show');
                      setOpenNodes(
                        new Set(traceTree?.criticalPath.map((n) => n.id)),
                      );
                      setAutoExpandMode(AutoExpandMode.CRITICAL_PATH);
                    }}
                    icon={<IconExpandList />}
                    description="Expands the path containing a bottleneck for this trace."
                  >
                    Expand Critical Path
                  </MenuItem>
                  {traceErrorsCount > 0 && (
                    <MenuItem
                      onClick={() => {
                        setAutoExpandMode(AutoExpandMode.ALL_ERRORS);
                      }}
                      icon={<IconExpandList />}
                    >
                      Expand all errors
                    </MenuItem>
                  )}
                  {traceTree?.hasRouterInternals && (
                    <>
                      <MenuDivider />
                      <MenuItemOption
                        value={routerInternalsSelection}
                        type="checkbox"
                        isChecked={Boolean(routerInternalsSelection === 'show')}
                        onClick={() => {
                          if (routerInternalsSelection === 'show') {
                            setRouterInternalSelection('hide');
                          } else {
                            setRouterInternalSelection('show');
                          }
                        }}
                      >
                        Show Router Internals
                      </MenuItemOption>
                    </>
                  )}
                </MenuList>
              </MenuContent>
            </Menu>
          )}
        </ButtonGroup>
      </CardHeader>
      <CardBody>
        <QueryTraceContent
          traceTree={traceTree}
          loading={loading}
          hasOtherTraces={hasOtherTraces}
          openNodes={openNodes}
          onOpenChange={onOpenChangeCallback}
        />
      </CardBody>
    </Card>
  );
};
